import React, { FC } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import i18n from 'i18n-js';
import { font } from '@styles/fonts';
import { scale } from '@components/frame';
import { isPhone, isWeb } from '@helpers';
import { getBoxShadowStyle } from '@styles/shadow';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import Svg from '@styles/icons/svg';
import TouchableElement from '@components/common/touchableElement';
import { ChangeSummaryPanelProps } from './types';
import { useManageChangeSummaryPanel } from './hooks/useManageChangeSummaryPanel';
import {
  CHIP_GAP,
  COLLAPSED_STRIP_MIN_HEIGHT,
  EXPANDED_MAX_HEIGHT,
} from './constants/changeSummaryPanel.constants';
import CategoryChip from './components/CategoryChip';
import CategorySection from './components/CategorySection';

const ChangeSummaryPanel: FC<ChangeSummaryPanelProps> = ({ C, preparedListItems, onJumpToChange, dialect, disabled }) => {
  const isTablet = !isPhone && !isWeb;
  const {
    isExpanded,
    toggleExpanded,
    hasChanges,
    visibleChipCategories,
    visibleCategoryGroups,
    expandedItemWidth,
    handleExpandedLayout,
    handleEntryPress,
  } = useManageChangeSummaryPanel({
    preparedListItems,
    onJumpToChange,
    dialect,
  });

  const containerStyle = [
    styles.container,
    getBoxShadowStyle({ type: 'Field', C }),
    {
      backgroundColor: C('Background/Light/Field'),
      borderRadius: scale(SCREEN_BORDER_RADIUS),
      opacity: disabled ? 0.6 : 1,
    },
  ];

  if (!hasChanges) {
    return null;
  }

  const caretIcon = isExpanded ? 'chevronUp' : 'chevronBottomLight';
  const toggleLabel = i18n.t(isExpanded ? 'Hide details' : 'Show details');

  return (
    <View style={containerStyle}>
      <TouchableElement onPress={toggleExpanded} disabled={disabled}>
        {({ pressHoverColor }) => (
          <View
            style={[
              styles.strip,
              isTablet && styles.tabletStrip,
              isWeb && ({ cursor: disabled ? 'not-allowed' : 'pointer' } as object),
              { backgroundColor: C(pressHoverColor('Background/Light/Field')) },
            ]}
          >
            <View style={styles.chipsRow}>
              {visibleChipCategories.map(({ category, count }) => (
                <CategoryChip key={category} C={C} category={category} count={count} />
              ))}
            </View>
            <View style={styles.toggleRow}>
              <Text style={[font.Body, { color: C('Link/Light/Primary') }]}>{toggleLabel}</Text>
              <Svg icon={caretIcon} width={scale(14)} height={scale(14)} color="Link/Light/Primary" />
            </View>
          </View>
        )}
      </TouchableElement>

      {isExpanded && (
        <View style={[styles.expandedWrapper, { borderTopColor: C('Label/Light/Quinary') }]}>
          <ScrollView
            style={{ maxHeight: scale(EXPANDED_MAX_HEIGHT) }}
            contentContainerStyle={styles.expandedContent}
          >
            <View style={styles.sectionsList} onLayout={handleExpandedLayout}>
              {visibleCategoryGroups.map((group) => (
                <CategorySection
                  key={group.category}
                  C={C}
                  category={group.category}
                  entries={group.entries}
                  totalCount={group.totalCount}
                  itemWidth={expandedItemWidth}
                  onJumpToChange={handleEntryPress}
                />
              ))}
            </View>
          </ScrollView>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    overflow: 'hidden',
    marginBottom: scale(16),
  },
  strip: {
    minHeight: scale(COLLAPSED_STRIP_MIN_HEIGHT),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: scale(16),
    paddingVertical: scale(10),
    gap: scale(12),
    flexWrap: 'wrap',
  },
  tabletStrip: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    flex: 1,
    gap: scale(CHIP_GAP),
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(6),
  },
  expandedWrapper: {
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  expandedContent: {
    paddingHorizontal: scale(16),
    paddingVertical: scale(12),
  },
  sectionsList: {
    gap: scale(16),
  },
});

export default React.memo(ChangeSummaryPanel);
